import type { ReactElement, ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { errorMessage, isApiError } from '../../lib/api'
import { useT } from '../../lib/i18n'
import { Button } from './Button'
import { Icon } from './Icon'

export interface ErrorStateProps {
  /** What failed, in the interface's voice. Defaults to `common.states.errorTitle`. */
  title?: string
  error?: unknown
  description?: ReactNode
  onRetry?: () => void
  /** Defaults to `common.actions.retry`. Pass one only when the verb should be specific. */
  retryLabel?: string
  className?: string
  compact?: boolean
  surface?: 'paper' | 'chrome'
}

/**
 * Errors state what failed and how to recover. They never apologise and never blame the user.
 * The error code is shown in mono so a support conversation has something to quote.
 */
export const ErrorState = ({
  title,
  error,
  description,
  onRetry,
  retryLabel,
  className,
  compact = false,
  surface = 'paper',
}: ErrorStateProps): ReactElement => {
  const t = useT()
  const heading = title ?? t('common.states.errorTitle')
  // `errorMessage` returns the server's own sentence — English by contract, and the network's
  // verbatim words when a provider failed. We translate our framing, never its text.
  const detail = description ?? (error !== undefined ? errorMessage(error) : undefined)
  const code = isApiError(error) ? error.code : null

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        compact ? 'py-6' : 'py-12',
        className,
      )}
    >
      <Icon name="alert" size={compact ? 20 : 24} className="text-down-deep" />
      <div className="flex flex-col gap-1">
        <p className={cn('t-h3', surface === 'chrome' ? 'text-chrome-ink' : 'text-ink')}>
          {heading}
        </p>
        {detail ? (
          <p
            className={cn(
              't-small max-w-md',
              surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
            )}
          >
            {detail}
          </p>
        ) : null}
        {code ? <p className="t-mono text-ink-mute">{code}</p> : null}
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} iconLeft={<Icon name="refresh" size={14} />}>
          {retryLabel ?? t('common.actions.retry')}
        </Button>
      ) : null}
    </div>
  )
}

export interface InlineErrorProps {
  children: ReactNode
  className?: string
}

/** A one-line failure inside a form or a row. */
export const InlineError = ({ children, className }: InlineErrorProps): ReactElement => (
  <p role="alert" className={cn('t-small flex items-center gap-1.5 text-down-deep', className)}>
    <Icon name="alert" size={14} />
    {children}
  </p>
)
